import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DataGrid } from '@mui/x-data-grid';
import { GET } from '../../../../configs/api';
import { api } from '../../../../utils/constants';
import { getClientChipStyles } from '../../../../utils/clientColors';
import { getStatusLabel } from '../../../../utils/jobStatuses';
import {
  getTechnicianDisplayName,
  formatCurrency,
  JOB_KPI_METRIC_LABELS,
} from './clientEarningsUtils';

const formatDateCell = (value) => {
  if (!value) return '—';
  const raw = typeof value === 'string' ? value : new Date(value).toISOString();
  return raw.slice(0, 10);
};

const getJobTechnicianNames = (job) => {
  const techs = job?.technicians || [];
  return techs
    .map((t) => getTechnicianDisplayName(t))
    .filter(Boolean);
};

const normalizeJobTrade = (trade) => {
  const trimmed = typeof trade === 'string' ? trade.trim() : '';
  return trimmed || 'Unspecified';
};

const moneyCell = (value, { color } = {}) => {
  const numValue = Number(value) || 0;
  return (
    <Typography
      sx={{
        color: color || 'inherit',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        fontSize: 13,
      }}
    >
      {formatCurrency(numValue, { fractionDigits: 0 })}
    </Typography>
  );
};

/**
 * Drill-down list for client Earnings KPIs, paid/unpaid, or status chips.
 */
export default function ClientJobsDrilldownDialog({
  open,
  clientId,
  metric,
  statusId,
  statusLabel,
  rangeLabel,
  queryParams,
  onClose,
  onOpenJob,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [tradeCounts, setTradeCounts] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const title = metric
    ? (JOB_KPI_METRIC_LABELS[metric] || 'Jobs')
    : (statusLabel || (statusId != null ? getStatusLabel(statusId) : 'Jobs'));

  useEffect(() => {
    if (!open) {
      setJobs([]);
      setTotal(0);
      setTradeCounts([]);
      setSelectedTrade(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    if (!clientId || (!metric && statusId == null)) return undefined;

    const controller = new AbortController();
    let active = true;

    const fetchJobs = async () => {
      setLoading(true);
      setError(null);
      setSelectedTrade(null);

      const params = {
        ...queryParams,
        limit: 500,
      };
      if (metric) {
        params.metric = metric;
      } else {
        params.status = statusId;
      }

      const res = await GET(
        `${api.getClientEarningsJobs}/${clientId}/earnings-jobs`,
        params,
        { signal: controller.signal },
      );

      if (!active || res.aborted) return;

      if (res.error || res.status !== 200) {
        setError(res.message || 'Failed to load jobs');
        setJobs([]);
        setTotal(0);
        setTradeCounts([]);
        setLoading(false);
        return;
      }
      setJobs(res.data?.jobs || []);
      setTotal(typeof res.data?.total === 'number' ? res.data.total : (res.data?.jobs || []).length);
      setTradeCounts(Array.isArray(res.data?.tradeCounts) ? res.data.tradeCounts : []);
      setLoading(false);
    };

    fetchJobs();
    return () => {
      active = false;
      controller.abort();
    };
  }, [open, clientId, metric, statusId, queryParams]);

  const filteredJobs = useMemo(() => {
    if (!selectedTrade) return jobs;
    return jobs.filter((job) => normalizeJobTrade(job.trade) === selectedTrade);
  }, [jobs, selectedTrade]);

  const handleTradeChipClick = (trade) => {
    setSelectedTrade((prev) => (prev === trade ? null : trade));
  };

  const columns = useMemo(() => [
    {
      field: 'client_name',
      headerName: 'Client',
      width: 150,
      valueGetter: (_value, row) => row.clientName || row.client_name || 'Unknown',
      renderCell: (params) => {
        const rowClientId = params.row.client_id;
        const label = params.value || 'Unknown';
        const { bgColor, textColor, borderColor } = getClientChipStyles(rowClientId);
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: 24,
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
            }}
          />
        );
      },
    },
    {
      field: 'work_order',
      headerName: 'Work Order',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
          {params.row?.is_locked && (
            <Tooltip title="Job locked - read only" arrow placement="top">
              <LockIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
          <span>{params.value || '—'}</span>
        </Box>
      ),
    },
    {
      field: 'unlocked',
      headerName: 'Unlocked',
      width: 90,
      sortable: true,
      valueGetter: (_value, row) => (row.unlocked ? 'Yes' : 'No'),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row?.unlocked ? (
            <Tooltip title="Manually unlocked" arrow placement="top">
              <LockOpenIcon sx={{ fontSize: 18, color: 'success.main' }} />
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
    {
      field: 'statusLabel',
      headerName: 'Status',
      width: 140,
      valueGetter: (_value, row) => row.statusLabel || getStatusLabel(row.status) || '',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 220,
      valueGetter: (_value, row) => row.description || '',
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap title={params.value}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'trade',
      headerName: 'Trade',
      width: 120,
      valueGetter: (_value, row) => row.trade || '',
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5 }} noWrap title={params.value}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      valueGetter: (_value, row) => row.notes || '',
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }} noWrap title={params.value}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'technicians',
      headerName: 'Technicians',
      width: 160,
      sortable: false,
      valueGetter: (_value, row) => getJobTechnicianNames(row).join(', '),
      renderCell: (params) => (
        <Typography sx={{ fontSize: 12.5 }} noWrap title={params.value}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Create Date',
      width: 120,
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'invoiced_date',
      headerName: 'Invoiced Date',
      width: 120,
      valueFormatter: (value) => formatDateCell(value),
    },
    {
      field: 'nte',
      headerName: 'NTE',
      width: 110,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => moneyCell(params.value, { color: '#22c55e' }),
    },
    {
      field: 'total_cost',
      headerName: 'Total Cost',
      width: 120,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => moneyCell(params.value),
    },
    {
      field: 'technicians_cost',
      headerName: 'Technicians Cost',
      width: 140,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => moneyCell(params.value),
    },
    {
      field: 'parts_cost',
      headerName: 'Parts Cost',
      width: 120,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => moneyCell(params.value),
    },
    {
      field: 'profit',
      headerName: 'Profit',
      width: 110,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const num = Number(params.value) || 0;
        return moneyCell(num, { color: num >= 0 ? '#22c55e' : '#ef4444' });
      },
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      width: 120,
      valueFormatter: (value) => value || '—',
    },
    {
      field: 'paid',
      headerName: 'Paid',
      width: 90,
      valueGetter: (_value, row) => (row.paid ? 'Yes' : 'No'),
      renderCell: (params) => (
        <Typography sx={{ fontSize: 13, display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.paid ? 'Yes' : 'No'}
        </Typography>
      ),
    },
  ], []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '80vw',
          maxWidth: '80vw',
          height: '80vh',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }} noWrap>
            {title}
          </Typography>
          {rangeLabel && (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 400 }}>
              {statusId != null && !metric ? 'Not limited by date range' : rangeLabel}
              {!loading && !error ? ` · ${total} job${total === 1 ? '' : 's'}` : ''}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 1.5, sm: 2 },
          pb: 2,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && jobs.length === 0 && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No jobs match this selection for the selected filters.
          </Typography>
        )}
        {!loading && !error && jobs.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {tradeCounts.length > 0 && (
              <Box sx={{ mb: 1, flexShrink: 0 }}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.75 }}>
                  Trade totals are for all matching jobs. The list shows up to 500.
                  {' '}Click a trade to filter the list; click again to clear.
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.75,
                  }}
                >
                  {tradeCounts.map((item) => {
                    const selected = selectedTrade === item.trade;
                    return (
                      <Chip
                        key={item.trade}
                        size="small"
                        clickable
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        label={`${item.trade}: ${item.count}`}
                        onClick={() => handleTradeChipClick(item.trade)}
                        sx={{
                          height: 24,
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: '6px',
                          ...(!selected
                            ? { bgcolor: isDark ? '#1e293b' : '#f1f5f9' }
                            : {}),
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1, flexShrink: 0 }}>
              {selectedTrade
                ? `Showing ${filteredJobs.length} of ${jobs.length} loaded job${jobs.length === 1 ? '' : 's'} for ${selectedTrade}${total > jobs.length ? ` (${total} match overall)` : ''}`
                : total > jobs.length
                  ? `Showing ${jobs.length} of ${total}`
                  : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`}
            </Typography>
            {filteredJobs.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 3, textAlign: 'center' }}>
                None of the loaded jobs match this trade
                {total > jobs.length ? ' (list shows up to 500 of all matching jobs).' : '.'}
              </Typography>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  border: '1px solid',
                  borderColor: isDark ? '#334155' : '#e7e7e7',
                  borderRadius: 1,
                }}
              >
                <DataGrid
                  key={selectedTrade || 'all-trades'}
                  rows={filteredJobs}
                  columns={columns}
                  getRowId={(row) => row.id}
                  density="compact"
                  disableRowSelectionOnClick
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                  onRowClick={(params) => onOpenJob?.(params.row)}
                  sx={{
                    border: 'none',
                    height: '100%',
                    '& .MuiDataGrid-row': { cursor: onOpenJob ? 'pointer' : 'default' },
                    '& .MuiDataGrid-cell': { outline: 'none !important' },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
